import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { API_BASE_URL, WS_URL, authHeaders, getAuthToken } from '@/services/config';

// Types
export interface SharedCartItem {
  id: string;
  sharedCartId: string;
  productId: string;
  variantId?: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  currency: string;
  status: 'active' | 'removed' | 'pending' | 'disputed';
  priority: number;
  notes?: string;
  addedByUserId?: string;
  addedByGuestId?: string;
  addedAt: string;
  updatedAt: string;
  product?: {
    id: string;
    title: string;
    description: string;
    images: string[];
    price: number;
  };
  votes?: CartItemVote[];
}

export interface CartItemVote {
  userId?: string;
  guestId?: string;
  vote: 'upvote' | 'downvote' | 'neutral';
  comment?: string;
  votedAt: string;
}

export interface SharedCartParticipant {
  id: string;
  sharedCartId: string;
  userId?: string;
  guestId?: string;
  displayName: string;
  role: 'host' | 'moderator' | 'participant' | 'observer';
  status: 'active' | 'inactive' | 'blocked' | 'left';
  permissions: {
    canAddItems: boolean;
    canRemoveItems: boolean;
    canEditQuantity: boolean;
    canInviteOthers: boolean;
    canKickOthers: boolean;
    canEditSettings: boolean;
    canEndSession: boolean;
  };
  joinedAt: string;
  lastSeenAt?: string;
  isOnline?: boolean;
}

export interface SharedCart {
  id: string;
  vendorId: string;
  hostUserId: string;
  sessionName: string;
  status: 'active' | 'paused' | 'completed' | 'expired';
  expiresAt?: string;
  settings: {
    allowGuestUsers: boolean;
    requireApproval: boolean;
    maxParticipants: number;
    autoExpireHours: number;
    allowItemRemoval: boolean;
    allowQuantityEdit: boolean;
    enableChat: boolean;
    enableVoting: boolean;
    notifyOnChanges: boolean;
    shareMode: 'open' | 'invite_only' | 'public';
  };
  createdAt: string;
  updatedAt: string;
  participants: SharedCartParticipant[];
  items: SharedCartItem[];
}

export interface SharedCartSummary {
  totalItems: number;
  totalValue: number;
  currency: string;
  participantCount: number;
  activeVotes: number;
  lastActivity?: string;
  itemsByCategory: Record<string, number>;
  topContributors: {
    userId?: string;
    guestId?: string;
    displayName: string;
    itemsAdded: number;
    totalValue: number;
  }[];
}

export interface WebSocketMessage {
  type: string;
  sessionId?: string;
  userId?: string;
  vendorId?: string;
  target?: string;
  data?: any;
  timestamp: string;
}

// Context
interface SharedCartContextType {
  // State
  sharedCart: SharedCart | null;
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;
  onlineParticipants: Set<string>;
  
  // WebSocket connection
  connect: (cartId: string) => void;
  disconnect: () => void;
  
  // Cart actions
  createSharedCart: (vendorId: string, sessionName: string, settings?: Partial<SharedCart['settings']>) => Promise<SharedCart>;
  joinSharedCart: (cartId: string, displayName: string, isGuest?: boolean) => Promise<void>;
  joinByInviteCode: (inviteCode: string, displayName: string) => Promise<void>;
  leaveSharedCart: () => Promise<void>;
  
  // Item actions
  addItem: (productId: string, quantity: number, variantId?: string, notes?: string) => Promise<void>;
  removeItem: (itemId: string) => Promise<void>;
  updateItemQuantity: (itemId: string, quantity: number) => Promise<void>;
  voteOnItem: (itemId: string, vote: 'upvote' | 'downvote' | 'neutral', comment?: string) => Promise<void>;
  
  // Real-time updates
  sendMessage: (type: string, data?: any) => void;
  // Subscribe to every incoming WebSocket message; returns an unsubscribe fn
  addMessageListener: (listener: (message: WebSocketMessage) => void) => () => void;
}


// The session you are in survives a refresh. A party is a room you are
// standing in, and a reload — or a phone locking itself — should not throw
// you back out to the front door asking for the code again.
const ACTIVE_CART_KEY = 'heybuddy.activeCart';

function rememberCart(cartId: string, userId: string) {
  try {
    localStorage.setItem(ACTIVE_CART_KEY, JSON.stringify({ cartId, userId }));
  } catch {
    // Private browsing and full quotas just mean no rejoin on reload.
  }
}

function forgetCart() {
  try {
    localStorage.removeItem(ACTIVE_CART_KEY);
  } catch {
    // Nothing to do; the stale entry is discarded on the next read anyway.
  }
}

function rememberedCart(userId: string): string | null {
  try {
    const raw = localStorage.getItem(ACTIVE_CART_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as { cartId?: string; userId?: string };
    // Only your own session: a shared computer must not drop the next
    // person into the last person's party.
    if (!stored.cartId || stored.userId !== userId) {
      forgetCart();
      return null;
    }
    return stored.cartId;
  } catch {
    forgetCart();
    return null;
  }
}

// Backend nil slices serialize as null; normalize to arrays before storing.
function normalizeCart(cart: any): SharedCart {
  return {
    ...cart,
    items: cart.items ?? [],
    participants: cart.participants ?? [],
    activities: cart.activities ?? [],
  };
}

const SharedCartContext = createContext<SharedCartContextType | null>(null);

export const useSharedCart = () => {
  const context = useContext(SharedCartContext);
  if (!context) {
    throw new Error('useSharedCart must be used within a SharedCartProvider');
  }
  return context;
};

// Provider component
interface SharedCartProviderProps {
  children: React.ReactNode;
}

export const SharedCartProvider: React.FC<SharedCartProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [sharedCart, setSharedCart] = useState<SharedCart | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [onlineParticipants, setOnlineParticipants] = useState<Set<string>>(new Set());
  
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const messageListenersRef = useRef<Set<(message: WebSocketMessage) => void>>(new Set());

  const addMessageListener = useCallback(
    (listener: (message: WebSocketMessage) => void) => {
      messageListenersRef.current.add(listener);
      return () => {
        messageListenersRef.current.delete(listener);
      };
    },
    []
  );


  // WebSocket connection management
  const connect = useCallback((cartId: string) => {
    if (!user) {
      setError('User not authenticated');
      return;
    }

    // Logging out clears the token while a socket may still be retrying.
    // Reconnecting without credentials just gets rejected five times over.
    const token = getAuthToken();
    if (!token) {
      return;
    }

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return; // Already connected
    }

    // Identity comes from the JWT; the server ignores client-supplied user ids
    const wsUrl = `${WS_URL}?token=${encodeURIComponent(token)}&sessionId=${cartId}&vendorId=${sharedCart?.vendorId || ''}`;
    
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        setError(null);
        reconnectAttempts.current = 0;
        
        // Send ping to verify connection
        sendMessage('ping');
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          handleWebSocketMessage(message);
          // Fan out to feature subscribers (kanban, reactions, chat, …)
          messageListenersRef.current.forEach(listener => {
            try {
              listener(message);
            } catch (listenerErr) {
              console.error('WebSocket listener error:', listenerErr);
            }
          });
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err);
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
        
        // Attempt to reconnect
        if (reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.pow(2, reconnectAttempts.current) * 1000; // Exponential backoff
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current++;
            connect(cartId);
          }, delay);
        } else {
          setError('Connection lost. Please refresh the page.');
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setError('Connection error occurred');
      };
    } catch (err) {
      console.error('Failed to create WebSocket connection:', err);
      setError('Failed to connect to real-time service');
    }
  }, [user, sharedCart?.vendorId]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    setIsConnected(false);
    reconnectAttempts.current = 0;
  }, []);

  const sendMessage = useCallback((type: string, data?: any) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const message: WebSocketMessage = {
        type,
        data,
        // Peer-targeted messages (WebRTC signaling) carry the recipient at
        // the top level so the hub can route them
        target: data?.target,
        timestamp: new Date().toISOString(),
      };

      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback((message: WebSocketMessage) => {
    switch (message.type) {
      case 'user_joined':
        if (message.userId) {
          setOnlineParticipants(prev => new Set([...prev, message.userId!]));
        }
        if (message.data?.participant) {
          setSharedCart(prev => {
            if (!prev) return prev;
            
            const existingParticipantIndex = prev.participants.findIndex(
              p => p.id === message.data.participant.id
            );
            
            if (existingParticipantIndex >= 0) {
              // Update existing participant
              const updatedParticipants = [...prev.participants];
              updatedParticipants[existingParticipantIndex] = {
                ...updatedParticipants[existingParticipantIndex],
                ...message.data.participant,
                isOnline: true,
              };
              
              return {
                ...prev,
                participants: updatedParticipants,
              };
            } else {
              // Add new participant
              return {
                ...prev,
                participants: [
                  ...prev.participants,
                  { ...message.data.participant, isOnline: true },
                ],
              };
            }
          });
          
          // Add to online participants
          setOnlineParticipants(prev => new Set([...prev, message.userId || '']));
        }
        break;

      case 'user_left':
        if (message.userId) {
          setOnlineParticipants(prev => {
            const updated = new Set(prev);
            updated.delete(message.userId!);
            return updated;
          });
          
          // Update participant status
          setSharedCart(prev => {
            if (!prev) return prev;
            
            return {
              ...prev,
              participants: prev.participants.map(p => 
                (p.userId === message.userId || p.guestId === message.userId)
                  ? { ...p, isOnline: false }
                  : p
              ),
            };
          });
        }
        break;

      case 'item_added':
        if (message.data?.item) {
          setSharedCart(prev => {
            if (!prev) return prev;
            
            return {
              ...prev,
              items: [...prev.items, message.data.item],
            };
          });
        }
        break;

      case 'item_removed':
        if (message.data?.item) {
          setSharedCart(prev => {
            if (!prev) return prev;
            
            return {
              ...prev,
              items: prev.items.map(item => 
                item.id === message.data.item.id
                  ? { ...item, status: 'removed' as const }
                  : item
              ),
            };
          });
        }
        break;

      case 'item_updated':
      case 'quantity_changed':
        if (message.data?.item) {
          setSharedCart(prev => {
            if (!prev) return prev;
            
            return {
              ...prev,
              items: prev.items.map(item => 
                item.id === message.data.item.id
                  ? { ...item, ...message.data.item }
                  : item
              ),
            };
          });
        }
        break;

      case 'session_state':
        if (message.data?.participants) {
          const participantIds = message.data.participants.map((p: any) => p.userId || p.guestId);
          setOnlineParticipants(new Set(participantIds));
        }
        break;

      case 'pong':
        // Keep connection alive
        break;

      default:
        console.log('Unhandled message type:', message.type, message);
    }
  }, []);

  // API methods
  const apiCall = async (endpoint: string, options: RequestInit = {}) => {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(),
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      // The API puts the generic label in `error` and the reason a human
      // needs — "the cart is locked", "not a participant" — in `details`.
      throw new Error(errorData.details || errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
  };

  const createSharedCart = async (
    vendorId: string, 
    sessionName: string, 
    settings?: Partial<SharedCart['settings']>
  ): Promise<SharedCart> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiCall(`/shared-carts?vendorId=${vendorId}`, {
        method: 'POST',
        body: JSON.stringify({
          sessionName,
          hostDisplayName: user?.displayName || user?.email || 'Host',
          settings,
        }),
      });

      const cart = normalizeCart(response.data);
      setSharedCart(cart);
      
      // Connect to WebSocket
      connect(cart.id);
      if (user) rememberCart(cart.id, user.id);
      
      return cart;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create shared cart';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const joinByInviteCode = async (inviteCode: string, displayName: string): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiCall(`/invites/${encodeURIComponent(inviteCode.trim())}/accept`, {
        method: 'POST',
        body: JSON.stringify({ displayName }),
      });

      const cartId = response.data?.sharedCartId;
      if (!cartId) {
        throw new Error('Invite accepted but no session was returned');
      }

      const cartResponse = await apiCall(`/shared-carts/${cartId}`);
      setSharedCart(normalizeCart(cartResponse.data));
      connect(cartId);
      if (user) rememberCart(cartId, user.id);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to join with invite code';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const joinSharedCart = async (cartId: string, displayName: string, isGuest: boolean = false): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      // Join first — reading a cart requires being a participant
      await apiCall(`/shared-carts/${cartId}/join`, {
        method: 'POST',
        body: JSON.stringify({
          displayName,
          isGuest,
        }),
      });

      // Then load the cart details
      const cartResponse = await apiCall(`/shared-carts/${cartId}`);
      setSharedCart(normalizeCart(cartResponse.data));

      // Connect to WebSocket
      connect(cartId);
      if (user) rememberCart(cartId, user.id);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to join shared cart';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Logging out leaves the party: drop the socket and the room rather than
  // holding a session the browser no longer has credentials for.
  useEffect(() => {
    if (user) return;
    disconnect();
    setSharedCart(null);
    setOnlineParticipants(new Set());
  }, [user, disconnect]);

  // Walk back into the room you were already in. Runs once the user is
  // known; a session that has since been checked out or expired is dropped
  // rather than reopened, since there is nothing left to shop.
  useEffect(() => {
    if (!user || sharedCart) return;
    // A shopper arriving from a storefront is sent straight to the party
    // the proxy opened for them; everyone else returns to the room they
    // were already in.
    const arriving = new URLSearchParams(window.location.search).get('cart');
    const cartId = arriving || rememberedCart(user.id);
    if (!cartId) return;

    let cancelled = false;
    setIsLoading(true);

    apiCall(`/shared-carts/${cartId}`)
      .then(response => {
        if (cancelled) return;
        const cart = normalizeCart(response.data);
        if (cart.status !== 'active' && cart.status !== 'paused') {
          forgetCart();
          return;
        }
        setSharedCart(cart);
        connect(cart.id);
        rememberCart(cart.id, user.id);
      })
      .catch(() => {
        // Gone, expired, or no longer ours.
        if (!cancelled) forgetCart();
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, sharedCart]);

  const leaveSharedCart = async (): Promise<void> => {
    if (!sharedCart) return;

    try {
      await apiCall(`/shared-carts/${sharedCart.id}/leave`, {
        method: 'POST',
      });

      // Disconnect WebSocket
      disconnect();
      setSharedCart(null);
      forgetCart();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to leave shared cart';
      setError(errorMessage);
      throw err;
    }
  };

  const addItem = async (
    productId: string, 
    quantity: number, 
    variantId?: string, 
    notes?: string
  ): Promise<void> => {
    if (!sharedCart) return;

    try {
      await apiCall(`/shared-carts/${sharedCart.id}/items`, {
        method: 'POST',
        body: JSON.stringify({
          productId,
          quantity,
          variantId,
          notes,
          isGuest: !user,
        }),
      });

      // Real-time update will be handled via WebSocket
    } catch (err) {
      // Surfaced where the user acted, not as a session-wide failure.
      throw err;
    }
  };

  const removeItem = async (itemId: string): Promise<void> => {
    if (!sharedCart) return;

    try {
      await apiCall(`/shared-carts/${sharedCart.id}/items/${itemId}`, {
        method: 'DELETE',
      });

      // Real-time update will be handled via WebSocket
    } catch (err) {
      // Surfaced where the user acted, not as a session-wide failure.
      throw err;
    }
  };

  const updateItemQuantity = async (itemId: string, quantity: number): Promise<void> => {
    if (!sharedCart) return;

    try {
      await apiCall(`/shared-carts/${sharedCart.id}/items/${itemId}/quantity`, {
        method: 'PUT',
        body: JSON.stringify({ quantity }),
      });

      // Real-time update will be handled via WebSocket
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update item quantity';
      setError(errorMessage);
      throw err;
    }
  };

  const voteOnItem = async (
    itemId: string, 
    vote: 'upvote' | 'downvote' | 'neutral', 
    comment?: string
  ): Promise<void> => {
    if (!sharedCart) return;

    try {
      await apiCall(`/shared-carts/${sharedCart.id}/items/${itemId}/vote`, {
        method: 'POST',
        body: JSON.stringify({
          vote,
          comment,
          isGuest: !user,
        }),
      });

      // Real-time update will be handled via WebSocket
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to vote on item';
      setError(errorMessage);
      throw err;
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  const contextValue: SharedCartContextType = {
    // State
    sharedCart,
    isConnected,
    isLoading,
    error,
    onlineParticipants,
    
    // WebSocket connection
    connect,
    disconnect,
    
    // Cart actions
    createSharedCart,
    joinSharedCart,
    joinByInviteCode,
    leaveSharedCart,
    
    // Item actions
    addItem,
    removeItem,
    updateItemQuantity,
    voteOnItem,
    
    // Real-time updates
    sendMessage,
    addMessageListener,
  };

  return (
    <SharedCartContext.Provider value={contextValue}>
      {children}
    </SharedCartContext.Provider>
  );
};